import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function AdminCategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState({ name: "", description: "" });
  const [adding, setAdding] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [reassignTo, setReassignTo] = useState("");

  const load = useCallback(async () => {
    try {
      const response = await api.get("/admin/categories");
      setCategories(response.data);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Could not load categories");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateRow = (id, field, value) => {
    setCategories((current) => current.map((category) => (category.id === id ? { ...category, [field]: value } : category)));
  };

  const addCategory = async (event) => {
    event.preventDefault();
    if (!newCategory.name.trim()) {
      toast.error("Category name is required");
      return;
    }
    setAdding(true);
    try {
      const response = await api.post("/admin/categories", newCategory);
      setCategories((current) => [...current, { ...response.data, product_count: 0 }]);
      setNewCategory({ name: "", description: "" });
      toast.success(`Category added with URL /catalog/${response.data.slug}`);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Could not add category");
    } finally {
      setAdding(false);
    }
  };

  const saveCategory = async (category) => {
    setSavingId(category.id);
    try {
      const payload = {
        name: category.name,
        description: category.description,
        meta_title: category.meta_title,
        meta_description: category.meta_description,
      };
      if (category.slug?.trim()) payload.slug = category.slug;
      const response = await api.patch(`/admin/categories/${category.id}`, payload);
      setCategories((current) => current.map((item) => (item.id === category.id ? { ...item, ...response.data } : item)));
      toast.success("Category saved. Products using the old name were updated automatically.");
      load();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Could not save category");
    } finally {
      setSavingId(null);
    }
  };

  const requestDelete = async (category) => {
    try {
      await api.delete(`/admin/categories/${category.id}`);
      setCategories((current) => current.filter((item) => item.id !== category.id));
      toast.success("Category deleted");
    } catch (error) {
      if (error.response?.status === 409) {
        setDeleteTarget(category);
        setReassignTo("");
      } else {
        toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Could not delete category");
      }
    }
  };

  const confirmDelete = async (mode) => {
    try {
      const params = mode === "reassign" ? { reassign_to: reassignTo } : { clear: true };
      await api.delete(`/admin/categories/${deleteTarget.id}`, { params });
      toast.success("Category deleted");
      setDeleteTarget(null);
      load();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Could not delete category");
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100" data-testid="categories-management-page">
      <main className="mx-auto max-w-6xl px-4 py-8">
        <Link to="/admin" data-testid="categories-back-link" className="mb-6 inline-flex items-center gap-2 text-sm text-blue-300 transition-colors hover:text-blue-200">
          <ArrowLeft className="h-4 w-4" /> Back to admin dashboard
        </Link>
        <div className="mb-6">
          <h1 className="font-headline text-3xl font-bold text-white">Category Management</h1>
          <p className="mt-1 text-sm text-slate-400">Renaming a category updates all assigned products. Slugs power SEO URLs like /catalog/mugs-and-bottles.</p>
        </div>
        <form onSubmit={addCategory} className="mb-8 rounded-2xl border border-slate-800 bg-slate-900/80 p-5" data-testid="add-category-form">
          <h2 className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Add category</h2>
          <div className="grid gap-3 md:grid-cols-[1fr_2fr_auto]">
            <Input data-testid="new-category-name-input" placeholder="Name (required)" value={newCategory.name} onChange={(event) => setNewCategory({ ...newCategory, name: event.target.value })} className="border-slate-700 bg-slate-950 text-slate-100" />
            <Input data-testid="new-category-description-input" placeholder="Description (optional)" value={newCategory.description} onChange={(event) => setNewCategory({ ...newCategory, description: event.target.value })} className="border-slate-700 bg-slate-950 text-slate-100" />
            <Button data-testid="add-category-button" type="submit" disabled={adding} className="bg-blue-500 text-white hover:bg-blue-400">
              <Plus className="h-4 w-4" /> {adding ? "Adding..." : "Add"}
            </Button>
          </div>
        </form>
        <div className="space-y-3" data-testid="categories-list">
          {categories.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-800 bg-slate-900/60 p-8 text-center text-sm text-slate-500" data-testid="categories-empty-state">
              No categories yet. Add your first category above.
            </div>
          ) : (
            categories.map((category) => (
              <div key={category.id} className="rounded-2xl border border-slate-800 bg-slate-900/80 p-4" data-testid={`category-row-${category.id}`}>
                <div className="grid items-center gap-3 lg:grid-cols-[1fr_1.5fr_1fr_auto_auto_auto]">
                  <Input data-testid={`category-name-input-${category.id}`} value={category.name} onChange={(event) => updateRow(category.id, "name", event.target.value)} className="border-slate-700 bg-slate-950 text-slate-100" />
                  <Input data-testid={`category-description-input-${category.id}`} value={category.description || ""} onChange={(event) => updateRow(category.id, "description", event.target.value)} className="border-slate-700 bg-slate-950 text-slate-100" placeholder="Description" />
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-slate-500">/catalog/</span>
                    <Input data-testid={`category-slug-input-${category.id}`} value={category.slug || ""} onChange={(event) => updateRow(category.id, "slug", event.target.value)} className="border-slate-700 bg-slate-950 font-mono text-xs text-slate-100" />
                  </div>
                  <Badge className="border-slate-700 bg-slate-800 text-slate-300" data-testid={`category-product-count-${category.id}`}>{category.product_count || 0} products</Badge>
                  <Button data-testid={`category-save-button-${category.id}`} size="sm" onClick={() => saveCategory(category)} disabled={savingId === category.id} className="bg-blue-500 text-white hover:bg-blue-400">
                    <Save className="h-4 w-4" /> {savingId === category.id ? "Saving" : "Save"}
                  </Button>
                  <Button data-testid={`category-delete-button-${category.id}`} size="sm" variant="outline" aria-label={`Delete ${category.name}`} onClick={() => requestDelete(category)} className="border-red-500/30 bg-red-500/10 text-red-300 hover:bg-red-500/20">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="mt-3 grid gap-3 lg:grid-cols-2">
                  <Input data-testid={`category-meta-title-input-${category.id}`} value={category.meta_title || ""} onChange={(event) => updateRow(category.id, "meta_title", event.target.value)} className="border-slate-800 bg-slate-950 text-xs text-slate-300" placeholder="Meta title (SEO)" />
                  <Input data-testid={`category-meta-description-input-${category.id}`} value={category.meta_description || ""} onChange={(event) => updateRow(category.id, "meta_description", event.target.value)} className="border-slate-800 bg-slate-950 text-xs text-slate-300" placeholder="Meta description (SEO)" />
                </div>
              </div>
            ))
          )}
        </div>
      </main>
      <Dialog open={Boolean(deleteTarget)} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="border-slate-800 bg-slate-950 text-slate-100" data-testid="delete-category-dialog">
          <DialogHeader>
            <DialogTitle className="font-headline text-xl">Category is in use</DialogTitle>
            <DialogDescription className="text-slate-400">
              &quot;{deleteTarget?.name}&quot; is assigned to {deleteTarget?.product_count} products. Reassign them to another category or remove the category from those products.
            </DialogDescription>
          </DialogHeader>
          <select data-testid="reassign-category-select" value={reassignTo} onChange={(event) => setReassignTo(event.target.value)} className="h-10 rounded-md border border-slate-700 bg-slate-900 px-3 text-sm text-slate-100">
            <option value="">Select a category to reassign products...</option>
            {categories.filter((category) => category.id !== deleteTarget?.id).map((category) => (
              <option key={category.id} value={category.id}>{category.name}</option>
            ))}
          </select>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button data-testid="confirm-delete-reassign-button" disabled={!reassignTo} onClick={() => confirmDelete("reassign")} className="bg-blue-500 text-white hover:bg-blue-400">Reassign & delete</Button>
            <Button data-testid="confirm-delete-clear-button" variant="outline" onClick={() => confirmDelete("clear")} className="border-amber-500/30 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20">Remove from products & delete</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
